import {
  ForwardRefRenderFunction,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { FrisoFormHandleProps } from '@hhgtech/hhg-components/build/components/onboardingV2/frisoForm'
import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import { Text, Modal } from '@hhgtech/hhg-components/mantine'
import { Onboarding } from '@hhgtech/hhg-components/onboardingV2'
import { Close } from '@hhgtech/icons/core'
import { Box, UnstyledButton, rem } from '@mantine/core'
import { yupResolver } from '@mantine/form'
import { useViewportSize } from '@mantine/hooks'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'
import Cookies from 'js-cookie'
import { useIntl } from 'react-intl'

import { formatString } from 'api/axios/helpers'
import { PATHS } from 'api/paths'
import { callApiWithLead } from 'api/utils'
import { getLeadPath } from 'api/utils/paths'
import { showNotification } from 'components/molecules/notificationsHandler'
import ErrorBoundary from 'components/templates/navigatorLayout/errorBoundary'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { LOCALE_SPECS, MANTINE_MODAL_TARGET } from 'interfaces/constants'
import { AppContext } from 'state/appContext'

import { PROVINCE_MAPPING } from './constant'
import { onResendCode, saveLeadApi, verifyPhoneExistApi } from './helper'
import MainContent from './mainContent'
import { frisoOnboardingSchema } from './schema'
import { useSidisTracking } from './useSidisTracking'

export type FrisoFormFieldProps = {
  name?: string
  first_name?: string
  last_name?: string
  dob: Date
  phone: string
  email: string
  address: any
  term: boolean
}

export type Props = {
  type: string
  desktopBanner?: string
  desktopBannerRatio?: {
    width: number
    height: number
  }
  frisoModalRatio?: {
    width: number
    height: number
  }
  mobileBanner?: string
  mobileBannerRatio?: {
    width: number
    height: number
  }
  delayTime?: number
  isModal?: boolean
}

export type FrisoModalHandle = {
  open: () => void
  close: () => void
}

const _FrisoOnboarding: ForwardRefRenderFunction<FrisoModalHandle, Props> = (
  {
    type,
    desktopBanner = CommonGAssets.getAssetPath('friso/onboarding-banner.jpg'),
    mobileBanner = CommonGAssets.getAssetPath(
      'friso/onboarding-banner-mobile.jpg',
    ),

    mobileBannerRatio = {
      width: 375,
      height: 240,
    },
    desktopBannerRatio = {
      width: 480,
      height: 740,
    },
    delayTime = 3000,
    isModal = false,
  },
  ref,
) => {
  const { trackCloseBabyPoopModal } = useSidisTracking()
  const cookieName = 'friso_disable_' + type
  const successSubribe = 'friso_success_subribe'
  const successSkipOrLeavePage = 'friso_success_skip'
  const { locale } = useRouterExtend()
  const { formatMessage: f } = useIntl()
  const { height: viewportHeight } = useViewportSize()
  const isMobile = useIsMobile()
  const currentUrl = useCurrentUrl()
  const [opened, { open, close }] = useDisclosure(false)
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null)
  const [step, setStep] = useState(0)
  const [otpLoading, setOtpLoading] = useState(false)
  const [otpSubmitLoading, setOtpSubmitLoading] = useState(false)
  const [onBoardingFormValues, setOnBoardingFormValues] = useState<any>()
  const [successSubmit, setSuccessSubmit] = useState(false)
  const defaultError = f({ id: 'notification.comment.somethingWrong' })

  const formRef = useRef<FrisoFormHandleProps>(null)
  const now = dayjs()
  const nextDueDateMonth = now
    .month(now.month() + 10)
    .date(30)
    .hour(0)
    .minute(0)
    .second(0)
    .toDate()
  const lastBornMonth = now
    .month(now.month() - 6)
    .date(1)
    .hour(0)
    .minute(0)
    .second(0)
    .toDate()

  const frisoModalRatio = {
    width: viewportHeight > 700 ? 1128 : 1000,
    height: viewportHeight > 700 ? 740 : 620,
  }

  const onSkip = () => {
    !Cookies.get(successSkipOrLeavePage) && saveLeadApi()
    Cookies.set(cookieName, 'true')
    Cookies.set(successSkipOrLeavePage, 'true')
  }

  const onSuccess = ({ phone, dob }: FrisoFormFieldProps) => {
    Cookies.set(successSubribe, 'true')
    const dobDate = dayjs(dob)
    const isDob = dobDate.isSameOrBefore(dayjs())
    const dateFormat = dobDate.format('YYYY-MM-DD')
    window.location.href = formatString(
      process.env.NEXT_PUBLIC_FRISO_MINI_GAME_URL || '',
      {
        phone,
        ...(isDob
          ? {
              dob: dateFormat,
            }
          : {
              edd: dateFormat,
            }),
      },
    )
  }

  useImperativeHandle(ref, () => ({
    open,
    close,
  }))

  useEffect(() => {
    !Cookies.get(successSubribe) &&
      !Cookies.get(successSkipOrLeavePage) &&
      setTimeout(() => {
        open?.()
      }, delayTime)
  }, [])

  useEffect(() => {
    window.onbeforeunload = function () {
      if (!Cookies.get(successSkipOrLeavePage) && opened && !successSubmit) {
        saveLeadApi()
        Cookies.set(successSkipOrLeavePage, 'true')
      }
    }
  }, [opened])

  const {
    state: { userInfo },
  } = useContext(AppContext)

  const onFormSubmit = async (values: any) => {
    const { address, email, name, dob, phone } = values
    const splitNameArr = name.split(' ')
    const last_name = splitNameArr[splitNameArr.length - 1]
    splitNameArr.pop()
    const first_name = splitNameArr.join(' ')

    if (!phone) return
    const phoneNumber = phone.replace('+84', '0')
    const formValues = {
      ...values,
      phone: phoneNumber,
      first_name,
      last_name,
      address,
      email,
      dob,
    }

    setOnBoardingFormValues(formValues)
    setPhoneNumber(phoneNumber)
    setOtpLoading(true)
    setOtpError(undefined)
    try {
      const response = await callApiWithLead(
        getLeadPath(PATHS.LEADGEN.POST_LEAD_SEND_OTP),
        'POST',
        {
          data: {
            phone_number: phoneNumber,
          },
        },
      )
      let respMessage = response?._messages?.[0]
      const _error_code = response?._error_code

      if (respMessage !== 'Send sms successfully') {
        if (_error_code === 'ERP_002') {
          // PHONE IS VERIFIED
          setSuccessSubmit(true)
          verifyPhoneExistApi(phoneNumber)
            .then((response) => {
              if (response?._messages?.[0] === 'Phone is available') {
                return saveLeadApi(formValues)
              }
            })
            .then(() => {
              onSuccess(formValues)
              setOtpLoading(false)
              close?.()
            })
          return Promise.reject(new Error('exist'))
        } else {
          if (respMessage === 'The number is invalid.') {
            respMessage =
              'Số điện thoại không hợp lệ. Vui lòng kiểm tra lại số điện thoại đã nhập.'
          }
          setOtpError(respMessage || defaultError)
          setOtpLoading(false)
        }
      }
      setOtpLoading(false)
    } catch (error) {
      setOtpLoading(false)
    }
  }

  const [otpError, setOtpError] = useState<string>()
  const onOtpSubmit = async (otp: string) => {
    setOtpError(undefined)
    setOtpSubmitLoading(true)

    if (!phoneNumber) {
      return false
    }
    try {
      const res = await callApiWithLead(
        getLeadPath(PATHS.LEADGEN.POST_LEAD_VERIFY_OTP),
        'POST',
        {
          data: {
            phone_number: phoneNumber,
            otp_code: otp,
          },
        },
      )
      // - Wrong Phone Number
      // - This phone number was verified
      // - Verify successfully
      // - Verify OTP failed
      // - SMS OTP not support your country
      const respMessage = res?._messages[0]
      if (
        respMessage === 'Verify successfully' ||
        respMessage === 'This phone number was verified'
      ) {
        setSuccessSubmit(true)
        verifyPhoneExistApi(phoneNumber)
          .then((response) => {
            if (response?._messages?.[0] === 'Phone is available') {
              return saveLeadApi(onBoardingFormValues)
            }
          })
          .then(() => {
            onSuccess(onBoardingFormValues)
          })
      } else {
        const defaultError = f({ id: 'notification.comment.somethingWrong' })
        const error = res?._messages[0] || defaultError
        setOtpError(error)
        setOtpSubmitLoading(false)
        return error
      }
    } catch (error) {
      console.error(error)
      const defaultError = f({ id: 'notification.comment.somethingWrong' })

      showNotification({
        title: defaultError,
        message: '',
        type: 'danger',
      })
      setOtpSubmitLoading(false)
    }
    return undefined
  }

  useEffect(() => {
    const preloadList = [desktopBanner, mobileBanner]
    preloadList.forEach((image) => {
      const newImage = new Image()
      newImage.src = image
    })
  }, [])

  const mainContent = () => (
    <>
      <MainContent
        step={step}
        desktopBanner={desktopBanner}
        desktopBannerRatio={desktopBannerRatio}
        frisoModalRatio={frisoModalRatio}
        mobileBanner={mobileBanner}
        mobileBannerRatio={mobileBannerRatio}
        loading={otpLoading}
        onContinue={() => formRef?.current?.submitForm()}
      >
        <Onboarding.StepRenderer onStepChange={setStep}>
          <Onboarding.FrisoForm
            ref={formRef}
            onSubmit={onFormSubmit}
            alwaysRender
            validateSchema={yupResolver(frisoOnboardingSchema(f))}
            customTerms={
              <Text size={isMobile ? 'p3' : 'p4'}>
                {f(
                  {
                    id: 'onboarding.friso.term',
                  },
                  {
                    helloBacsi: (
                      <a target="_blank" title="Hello Bacsi">
                        {LOCALE_SPECS[locale]?.SITE_NAME_FORMATTED}
                      </a>
                    ),
                    brand: (
                      <a
                        target="_blank"
                        href="https://www.friso.com.vn/chinh-sach-bao-mat"
                        title="Friso"
                      >
                        Friso
                      </a>
                    ),
                  },
                )}
              </Text>
            }
            customFormTexts={{
              heading: f({
                id: 'onboarding.friso.heading',
                defaultMessage: 'Your information',
              }),
              description: f({
                id: 'onboarding.friso.description',
                defaultMessage:
                  'To stay up-to-date with our exclusive offers and exciting updates, please provide your information so we can share the latest news with you!',
              }),
            }}
            onClose={() => false}
            defaultValues={{
              phone: userInfo?.phone,
              email:
                userInfo?.email && !userInfo.email.endsWith('@facebook.com')
                  ? userInfo.email
                  : '',
            }}
            inputOrder={[
              {
                inputType: 'full_name',
                label: f({
                  id: 'onboarding.friso.full_name',
                  defaultMessage: `Mom's full name`,
                }),
                placeholder: f({
                  id: 'onboarding.friso.full_name_placeholder',
                  defaultMessage: 'Ví dụ: Nguyễn Anh Thư',
                }),
              },
              {
                inputType: 'dob',
                name: 'dob',
                label: f({
                  id: 'onboarding.friso.dob',
                  defaultMessage: 'Baby‘s DOB',
                }),
                withAsterisk: true,
                maxDate: nextDueDateMonth,
                minDate: lastBornMonth,
              },
              {
                inputType: 'phone',
                name: 'phone',
                label: f({
                  id: 'onboarding.friso.phone',
                  defaultMessage: 'Mom’s phone number',
                }),
              },
              {
                inputType: 'email',
                name: 'email',
                label: f({
                  id: 'onboarding.friso.email',
                  defaultMessage: 'Mom’s email',
                }),
                placeholder: f({
                  id: 'onboarding.friso.email_placeholder',
                  defaultMessage: 'email@email.com',
                }),
              },
              {
                inputType: 'address',
                label: f({
                  id: 'onboarding.friso.address',
                  defaultMessage: 'Address',
                }),
                className: 'address-input',
                provinces: PROVINCE_MAPPING as any,
                placeholder: {
                  province: f({ id: 'onboarding.friso.province' }),
                  district: f({ id: 'onboarding.friso.district' }),
                  ward: f({ id: 'onboarding.friso.ward' }),
                } as any,
              },
              {
                inputType: 'term',
                className: 'term-control',
              },
            ]}
            sx={(theme) => ({
              '&[data-active="false"]': {
                display: 'none !important',
              },
              '.friso-form': {
                justifyContent: 'space-between',
                '.half-col': {
                  [theme.fn.largerThan('md')]: {
                    width: 'calc((100% - 16px)/2) !important',
                  },
                },
                '.term-control': {
                  paddingTop: rem(10),
                },
                'div:last-child': {
                  marginBottom: '0 !important',
                },
                '.mantine-Select-item': {
                  fontSize: '14px',
                },
                '.mantine-Input-input': {
                  '&[data-invalid]::placeholder, &[data-invalid].mantine-DatePickerInput-input': {
                    color: '#737373',
                  },
                },
              },
              '.address-input > div': {
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                '.mantine-InputWrapper-root': {
                  width: '100%',
                  [theme.fn.largerThan('md')]: {
                    width: 'calc((100% - 32px)/3)',
                    '&:last-child': {
                      width: '100%',
                    },
                  },
                },
              },
            })}
          />
          <Onboarding.OtpForm.Phone
            loading={otpSubmitLoading}
            onSubmit={onOtpSubmit}
            onResendCode={onResendCode}
            otpError={otpError}
            onClose={onSkip}
            canSkip={false}
          />
        </Onboarding.StepRenderer>
      </MainContent>
      <UnstyledButton
        data-event-category="Leadgen Pop Up"
        data-event-action="Skip Click"
        data-event-label={currentUrl}
        onClick={() => {
          close()
          onSkip()
          trackCloseBabyPoopModal()
        }}
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          cursor: 'pointer',
          padding: rem(10),
        }}
      >
        <Close />
      </UnstyledButton>
    </>
  )

  return (
    <ErrorBoundary>
      <Onboarding.Provider>
        {isModal ? (
          <Modal
            target={MANTINE_MODAL_TARGET}
            opened={opened}
            onClose={() => false}
            styles={(theme) => ({
              header: {
                position: 'absolute',
                top: 0,
                right: 0,
                borderBottom: 0,
              },
              content: {
                flex: 'auto',
                background: 'transparent',
                [theme.fn.smallerThan('sm')]: {
                  maxHeight: '100%',
                  height: '100%',
                  position: 'relative',
                  borderRadius: 0,
                },
                [theme.fn.largerThan('sm')]: {
                  maxWidth: rem(frisoModalRatio.width),
                  width: '100%',
                  height: '100%',
                  maxHeight: rem(frisoModalRatio.height),
                  boxShadow: 'none',
                },
              },
              body: {
                padding: 0,
                width: '100%',
                height: '100%',
                [theme.fn.largerThan('sm')]: {
                  display: 'flex',
                  alignItems: 'center',
                },
              },
              inner: {
                padding: 0,
                [theme.fn.smallerThan('sm')]: {
                  display: 'flex',
                  alignItems: 'flex-end',
                },
              },
            })}
          >
            {mainContent()}
          </Modal>
        ) : (
          <Box>{mainContent()}</Box>
        )}
      </Onboarding.Provider>
    </ErrorBoundary>
  )
}

export default forwardRef(_FrisoOnboarding)
